<template>
  <div class="query__container">
    <RetractableScreenCoordinates ref="retractableScreenRef">
      <div class="dataview__table--wrap-normal">
        <DataView
          v-bind="props.tableConfig"
          ref="dataView"
          @refreshed="checkRefreshedValues()"
        >
          <template #header__center>
            <RegionalSearch
              class="query__regional-search"
              :model-value="regionalSearchValueFunc()"
              :provinces="provinces"
              :regions="regions"
              :search-label="props.searchLabel"
              :highway-label="props.highwayLabel"
              :highway-selection-options="props.highwaySelectionOptions"
              :town-label="props.townLabel"
              :town-selection="props.townSelection"
              :province-label="props.provinceLabel"
              :province-selection="props.provinceSelection"
              :region-label="props.regionLabel"
              :region-selection="props.regionSelection"
              @update:model-value="updateRegionalFilters"
            />
            <slot name="header"></slot>
          </template>
          <template #header__after>
          </template>
          <template
            v-if="props.useExpansion"
            #table__expansion="{data}"
          >
            <slot
              name="expansion"
              :data="data"
            >
            </slot>
          </template>
          <template
            v-if="props.useExpansion"
            #list__expansion="{data}"
          >
            <slot
              name="expansion"
              :data="data"
            >
            </slot>
          </template>
          <!-- Named field slots -->
          <!-- photoUrl from photoQuery -->
          <template #table__photoUrl__body="{data}">
            <slot
              name="field__photoUrl"
              :data="data"
            >
            </slot>
          </template>
          <template #list__photoUrl="{data}">
            <slot
              name="field__photoUrl"
              :data="data"
            >
            </slot>
          </template>
          <!-- end photoUrl -->
          <!-- blueFlag from beachesQuery -->
          <template #table__blueFlag__body="{data}">
            <slot
              name="field__blueFlag"
              :data="data"
            >
            </slot>
          </template>
          <template #list__blueFlag="{data}">
            <slot
              name="field__blueFlag"
              :data="data"
            >
            </slot>
          </template>
          <!-- end blueFlag -->
          <!-- hasDisabledAccess from beachesQuery -->
          <template #table__hasDisabledAccess__body="{data}">
            <slot
              name="field__hasDisabledAccess"
              :data="data"
            >
            </slot>
          </template>
          <template #list__hasDisabledAccess="{data}">
            <slot
              name="field__hasDisabledAccess"
              :data="data"
            >
            </slot>
          </template>
          <!-- end hasDisabledAccess -->
          <template #footer>
            <div class="query__paginator-container--big">
              <Paginator
                v-if="pagination"
                :rows="pagination.pageSize"
                :page-link-size="5"
                :total-records="pagination.totalResults"
                :rows-per-page-options="props.rowsPerPageOptions"
                @page="updatePagination"
              ></Paginator>
            </div>
            <div class="query__paginator-container--small">
              <Paginator
                v-if="pagination"
                :rows="pagination.pageSize"
                :page-link-size="1"
                :total-records="pagination.totalResults"
                @page="updatePagination"
              ></Paginator>
            </div>
          </template>
        </DataView>
      </div>
    </RetractableScreenCoordinates>
  </div>
</template>

<script setup>
import {ref, toRaw, computed, onMounted} from 'vue';
import {useStore} from 'vuex';
import {DataView} from '@sttedra_ui/framework';
import {getAllRegionProvinces} from '@/api/province/province.api';
import RetractableScreenCoordinates
  from '@/components/retractable-screen-coordinates/RetractableScreenCoordinates';
import Paginator from 'primevue/paginator';
import useHandleMOCError from '@/composables/use-handle-moc-error';
import {errorToastOptions} from '@/util-toast';
import RegionalSearch from '@/components/regional-search/RegionalSearch';
import {REGIONAL_SEARCH_PROPS} from '@/components/regional-search/regional-search-props';

// #region Props, Emits
const props = defineProps({
  ...REGIONAL_SEARCH_PROPS,
  nameFieldName: {
    type: String,
    default: () => 'name',
  },
  highwayFieldName: {
    type: String,
    default: () => 'highway',
  },
  townFieldName: {
    type: String,
    default: () => 'town',
  },
  provinceFieldName: {
    type: String,
    default: () => 'province',
  },
  regionFieldName: {
    type: String,
    default: () => 'region',
  },
  rowsPerPageOptions: {
    type: Array,
    default: () => [10, 20, 50],
  },
  useExpansion: {
    type: Boolean,
    default: false,
  },
  tableConfig: {
    type: Object,
    required: true,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const store = useStore();
const {handleMOCError} = useHandleMOCError();
const dataView = ref();
const retractableScreenRef = ref();
const regions = ref([]);
const provinces = ref([]);
const state$$ = computed(() =>
  !!dataView.value && !!dataView.value.state$$ ? dataView.value.state$$ : null,
);
const pagination = computed(() =>
  !!state$$?.value?.pagination?.pageIndex ? state$$.value.pagination : null,
);
const getFilter = (fieldName) => {
  const f = dataView?.value?.state$$?.advancedSearch?.filters?.find(
    (f) => f.field === fieldName,
  );
  return !!f ? f : null;
};
const filterValue = (fieldName) => {
  const f = getFilter(fieldName);
  return !!f?.value ? f.value : null;
};
const regionFromProvince = (provinceName) => {
  const foundProvince = provinces.value.find((p) => p.name === provinceName);
  return foundProvince?.region;
};
const checkRefreshedValues = () => {
  const province = filterValue(props.provinceFieldName);
  const region = filterValue(props.regionFieldName);
  if (!region && !!province) {
    updateRegionalFilters({province: null});
  }
  return;
};
const regionalSearchValueFunc = () => {
  const search = filterValue(props.nameFieldName);
  const highway = filterValue(props.highwayFieldName);
  const town = filterValue(props.townFieldName);
  const province = filterValue(props.provinceFieldName);
  const region = filterValue(props.regionFieldName);
  return {
    search,
    highway,
    town,
    province,
    region,
  };
};
// #endregion

// #region Functions
function updateAdvancedFilters(selectedFilters) {
  const fieldsArray = selectedFilters.filter((item) => item.value).map((item) => item.field);
  const fieldsString = `['${fieldsArray.join('\',\'')}']`;
  dataView.value?.state$$?.advancedSearch.filters.forEach((element) => {
    if (element.field === 'categoria') {
      element.value = fieldsString;
    }
  });
  dataView.value.debounceRefreshAndUpdate();
}
function updateRegionalFilters({search, highway, town, province, region}, debounce = true) {
  const newHighway = !!highway?.value?.highway ? highway.value.highway : highway;
  // const newTown = !!town?.value?.id ? town.value.id : town;
  // const newTown = !!town?.value?.name ? town.value.name : town;
  const newTown = !!town?.name ? town.name : town;
  const updateObject = {};
  if (search !== undefined) { // can be null
    updateObject[props.nameFieldName] = search;
  }
  if (newHighway !== undefined) { // can be null
    updateObject[props.highwayFieldName] = newHighway;
  }
  if (newTown !== undefined) { // can be null
    updateObject[props.townFieldName] = newTown;
  }
  if (province !== undefined) { // can be null
    updateObject[props.provinceFieldName] = province;
  }
  if (region !== undefined) { // can be null
    updateObject[props.regionFieldName] = region;
  }
  if (!region && !!province) {
    const foundRegion = regionFromProvince(province);
    if (!!foundRegion) {
      updateObject[props.regionFieldName] = foundRegion;
    }
  }
  state$$.value.updateAdvancedSearch(updateObject);
  if (debounce) {
    dataView.value.debounceRefreshAndUpdate();
  }
}
function updatePagination(data) {
  const {rows, page: page0Index} = data;
  const page = page0Index + 1;
  const pagination = dataView.value?.state$$?.pagination;
  if (!!pagination.pageIndex && !!page) {
    pagination.pageIndex = page;
  }
  if (!!pagination.pageSize && !!rows) {
    pagination.pageSize = rows;
  }
  dataView.value.debounceRefreshAndUpdate();
}
async function goCoordinates({coordinates, name, visible, zoom}) {
  const retractableCoordinatesFunc = retractableScreenRef.value?.goCoordinates ?? null;
  if (!!retractableCoordinatesFunc) {
    await retractableCoordinatesFunc({coordinates, name, visible, zoom});
  }
  return;
}
function toggleAdvancedSearch() {
  const st = state$$?.value;
  if (!!st) {
    st.advancedSearchShown = !st.advancedSearchShown;
  }
  return;
}
async function printRecord(id, name, getRecordPdfApi, extension = 'pdf') {
  const now = new Date();
  await store.dispatch('reports/addReportUrlRequest', {time: now, name, extension});
  const promise = getRecordPdfApi(id);
  promise
    .then(async ({url, time}) => {
      await store.dispatch('reports/completeReportUrl', {url, timeStart: now, timeFinish: time});
    })
    .catch(async (err) => {
      await store.dispatch('reports/removeReportUrl', {timeStart: now});
      const errorPrint = errorToastOptions('Error', 'Error al imprimir');
      return handleMOCError(err, errorPrint);
    });
}
async function printQuery(name, getQueryPdfApi, extension = 'pdf') {
  const now = new Date();
  await store.dispatch('reports/addReportUrlRequest', {time: now, name, extension});
  const state = toRaw(state$$.value);
  const params = state.getParams();
  const promise = getQueryPdfApi(params);
  promise
    .then(async ({url, time}) => {
      await store.dispatch('reports/completeReportUrl', {url, timeStart: now, timeFinish: time});
    })
    .catch(async (err) => {
      await store.dispatch('reports/removeReportUrl', {timeStart: now});
      const errorPrint = errorToastOptions('Error', 'Error al imprimir');
      return handleMOCError(err, errorPrint);
    });
}
// #endregion
// #region Effects, Hooks
onMounted(async () => {
  try {
    const {provinces: listProvinces, regions: listRegions} =
        await getAllRegionProvinces();
    provinces.value = listProvinces;
    regions.value = listRegions;
    if (window.screen.width <= 576) {
      setTimeout(() => (dataView.value.state$$.viewRef = 'list'), 10);
    }
  } catch (err) {
    const errorProvinces = errorToastOptions('Error', 'Error al cargar las provincias');
    handleMOCError(err, errorProvinces);
  }
});
// #endregion

// #region Provide, Expose
defineExpose({
  goCoordinates,
  toggleAdvancedSearch,
  printQuery,
  printRecord,
  updateAdvancedFilters,
});
// #endregion
</script>

<style lang="scss" scoped>
@import "src/styles";
.dataview__table--wrap-normal :deep(td.data-view-table__field) {
  white-space: normal;
}
.query__container {
  & :deep(th.query__header--align-right div.p-column-header-content) {
    justify-content: flex-end;
  }
  & :deep(td.query__header--align-right) {
    text-align: right;
  }
  & :deep(table.p-datatable-table) {
    border-collapse: separate;
    border-spacing: 0;
  }
  & :deep(th.p-resizable-column) {
    position: sticky !important;
    z-index: 100;
    top: 0;
    background-color: white;
    border-bottom: 2px solid $brand__color--lightblue;
  }
  & :deep(div.p-datatable-wrapper) {
    overflow-y: inherit;
  }
  & :deep(.basecard__content) {
    display: flex;
    flex-wrap: wrap;
  }
  & :deep(.basecard__content > div) {
    display: inline-block;
  }
  & :deep(.basecard__content > div:first-child) {
    width: 100%;
  }
  & :deep(.data-view__header__after > button) {
    margin-left: 0 !important;
  }
  & :deep(.data-view__header__after > button:first-of-type) {
    display: none;
  }
  & :deep(.data-view__advancedsearch__form) {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  & :deep(.data-view__advancedsearch__form .block--classification-item) {
    width: 100%;
    padding: 0 1rem;
    margin: 0.3rem 0;
    @include min-screen-width($brand__breakpoint--md) {
      width: 50%;
    }
    @include min-screen-width($brand__breakpoint--lg) {
      width: 25%;
    }
  }
  & :deep(.data-view__advancedsearch__form .block--classification-item__3column) {
    @include min-screen-width($brand__breakpoint--lg) {
      width: 33%;
    }
  }
  & :deep(.data-view__advancedsearch__content__actions) {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1rem;
  }
  & :deep(.data-view-actions-combo) {
    justify-content: center;
  }
  & :deep(.data-view__header__after) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
  & :deep(.data-view__header__center) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
  & :deep(.query__regional-search) {
    order: 2;
  }
  & :deep(.query__category-button) {
    order: 3;
    height: 32px;
    margin-left: 8px;
    @media only screen and (max-width: 670px) {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }
  & :deep(.query__category-select) {
    order: 3;
    margin-left: 10px;
    margin-bottom: 0.3rem;
    background-color: transparent;
    & :deep(.input-search::placeholder) {
      color: black;
    }
    & :deep(.p-multiselect) {
      background-color: transparent;
    }
    & :deep(.p-multiselect-label) {
      color: black;
      font-size: 14px;
    }
    @media only screen and (max-width: 670px) {
      font-size: medium;
      padding-top: 10px;
    }
  }
  & :deep(tr.p-datatable-row-expansion > td) {
    white-space: normal !important;
  }
  & :deep(.data-view-list__item__collapse .query__expansion-container) {
    margin: 0;
  }
  & :deep(.query__expansion-container) {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-evenly;
    width: 95%;
    background-color: rgba(231, 240, 255, 0.3764705882) !important;
    @include max-screen-width($brand__breakpoint--md) {
      margin: 0;
    }
    margin: 1rem 0.5rem 1rem 3rem;
    padding: 0.5rem;
  }
  & :deep(.query__expansion-field-container) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    padding: 0.4rem;
  }
  & :deep(.query__expansion-field-label) {
    font-weight: bold;
    padding-right: 1rem;
  }
  & :deep(.query__expansion-field-value) {
    max-width: 100%;
    /* padding: 0.2rem 0 0.2rem 1rem; */
  }
  & :deep(.p-paginator) {
    .p-paginator-page.p-highlight, .p-dropdown {
      background-color: $subtle-blue !important;
    }
  }
}
.query__paginator-container--small {
  display: block;
  @include min-screen-width($brand__breakpoint--sm) {
    display: none;
  }
}
.query__paginator-container--big {
  display: none;
  @include min-screen-width($brand__breakpoint--sm) {
    display: block;
  }
}
</style>
